import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { getCreditUsage, getTokenUsage, getWallet } from "../../../helpers/apis";
import { GET_CREDIT_USAGE, GET_TOKEN_USAGE, GET_WALLET } from "../queryKey";
import {
  InfoTooltip,
  useCancelPlanDowngradeMutation,
  useCheckoutSessionMutation,
  useWalletDepositConfirmMutation,
  useWalletDepositMutation,
} from "../utils";
import { Button, Tooltip, Card, CardContent } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { ComponentLoader } from "../../../components/loader";
import { useIntercom } from "react-use-intercom";
import { CustomPieChart } from "../GraphUtils";
import type { PieDataPoint } from "../GraphUtils";
import SliderSelect from "../CreditsSelectSlider";
import classNames from "classnames";
import type { WalletTransaction } from "../types";
import { EmptyScreen } from "../../warehouse/misc";
import PlanEmptyScreen from "../../../components/planEmptyScreen/PlanEmptyScreen";
import { OverageStatusCard } from "../components/OverageStatusCard";

const formatTokens = (tokens: number): string => {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(2)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return tokens.toLocaleString();
};

export const PlansTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const manage_credits = searchParams.get("manage_credits");
  const [postTransactionMessage, setPostTransactionMessage] = useState<
    | {
        message: string;
        type: string;
        pageMessage?: boolean;
        showCloseButton?: boolean;
      }
    | undefined
  >(undefined);

  const [totalAvailableExecutions, setTotalAvailableExecutions] = useState(0);
  const [totalExecutions, setTotalExecutions] = useState(0);
  const [nextRefreshDate, setNextRefreshDate] = useState("");
  const [totalPlanExecutions, setTotalPlanExecutions] = useState(0);
  const [availableGrantExecutions, setAvailableGrantExecutions] = useState(0);
  const [extraExecutions, setExtraExecutions] = useState(0);
  const [used, setUsed] = useState(0);
  const [currentPlan, setCurrentPlan] = useState("");
  const [currentTenure, setCurrentTenure] = useState("month");
  const [usedBarWidth, setUsedBarWidth] = useState(0);
  const [remainingBarWidth, setRemainingBarWidth] = useState(0);
  const [planChangeState, setPlanChangeState] = useState("");
  const [showManageSubscriptions, setShowManageSubscriptions] = useState(
    manage_credits === "true" || false
  );
  const [isEnterprise, setIsEnterprise] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount] = useState(0);

  const [depositAmount, setDepositAmount] = useState(10);

  const { show } = useIntercom();
  const checkoutSessionMutation = useCheckoutSessionMutation(
    setPostTransactionMessage,
    currentTenure,
    setPlanChangeState
  );

  const cancelPlanDowngradeMutation = useCancelPlanDowngradeMutation(
    setPostTransactionMessage,
    currentTenure,
    setPlanChangeState
  );

  const walletDepositMutation = useWalletDepositMutation();
  const walletConfirmMutation = useWalletDepositConfirmMutation(setPostTransactionMessage);
  const walletConfirmProcessed = useRef(false);

  const {
    data: creditUsage,
    isLoading,
    error,
  } = useQuery({
    queryKey: [GET_CREDIT_USAGE, retryCount],
    queryFn: getCreditUsage,
  });

  const { data: walletData } = useQuery({
    queryKey: [GET_WALLET],
    queryFn: getWallet,
  });

  const { data: tokenUsage } = useQuery({
    queryKey: [GET_TOKEN_USAGE],
    queryFn: getTokenUsage,
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (creditUsage) {
      // Check if API returned ok: false (no plan or expired plan case)
      if (!creditUsage || creditUsage?.ok === false) {
        setHasError(true);
        return;
      }

      setCurrentTenure(creditUsage?.tenure);
      setTotalAvailableExecutions(creditUsage?.total_available_executions);
      setTotalExecutions(creditUsage?.total_executions);
      setUsed(creditUsage?.used_executions);
      setTotalPlanExecutions(creditUsage?.plan_total_executions);
      setExtraExecutions(creditUsage?.extra_executions);
      setNextRefreshDate(creditUsage?.next_refresh_date);
      setCurrentPlan(creditUsage?.credit_type);
      setAvailableGrantExecutions(creditUsage?.available_grant);
      setPlanChangeState("");
      setIsEnterprise(creditUsage?.current_edition === "Enterprise");
      setHasError(false);
    }
  }, [creditUsage]);

  // Handle error state (replaces onError callback removed in TanStack Query v5)
  useEffect(() => {
    if (error) {
      setHasError(true);
    }
  }, [error]);

  const data: PieDataPoint[] = [
    { name: extraExecutions ? "extra" : "spent", value: used },
    { name: "remaining", value: totalAvailableExecutions },
  ];

  const handleSuccessMessageClose = () => {
    setPostTransactionMessage(undefined);
  };

  useEffect(() => {
    const walletDeposit = searchParams.get("wallet_deposit");
    if (walletDeposit === "success") {
      const sessionId = searchParams.get("session_id");
      if (sessionId && !walletConfirmProcessed.current) {
        walletConfirmProcessed.current = true;
        walletConfirmMutation.mutate({ checkoutSessionId: sessionId });
      }
      // Preserve tab param when clearing checkout params
      const tab = searchParams.get("tab");
      setSearchParams(tab ? { tab } : {}, { replace: true });
      const timeoutId = setTimeout(() => setPostTransactionMessage(undefined), 30000);
      return () => clearTimeout(timeoutId);
    } else if (walletDeposit === "canceled") {
      setPostTransactionMessage({
        message: "Wallet deposit was cancelled.",
        type: "warning",
        pageMessage: true,
        showCloseButton: true,
      });
      const tab = searchParams.get("tab");
      setSearchParams(tab ? { tab } : {}, { replace: true });
    } else if (searchParams.get("success")) {
      const sessionCheckoutID = searchParams.get("session_id");
      if (sessionCheckoutID) {
        try {
          checkoutSessionMutation.mutate({
            sessionCheckoutID: sessionCheckoutID,
            cancelled: false,
          });
        } catch (error) {
          console.error("Error calling checkout session mutation:", error);
          setPostTransactionMessage({
            message:
              "Failed to process checkout session. Please contact support if this issue persists.",
            type: "error",
            pageMessage: true,
            showCloseButton: true,
          });
        }
      }
      const timeoutId = setTimeout(() => {
        setPostTransactionMessage(undefined);
      }, 30000);
      return () => clearTimeout(timeoutId);
    } else if (searchParams.get("canceled")) {
      const sessionCheckoutID = searchParams.get("session_id");
      if (sessionCheckoutID) {
        try {
          checkoutSessionMutation.mutate({
            sessionCheckoutID: sessionCheckoutID,
            cancelled: true,
          });
        } catch (error) {
          console.error("Error calling checkout session mutation:", error);
          setPostTransactionMessage({
            message:
              "Failed to process cancelled checkout session. Please contact support if this issue persists.",
            type: "error",
            pageMessage: true,
            showCloseButton: true,
          });
        }
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Handle case when user has extra credits available
    if (extraExecutions > 0) {
      // Show full bar: used credits + extra credits
      const totalWithExtra = used + extraExecutions;
      const usedPercentage = (used / totalWithExtra) * 100;
      const extraPercentage = (extraExecutions / totalWithExtra) * 100;

      setUsedBarWidth(usedPercentage);
      setRemainingBarWidth(extraPercentage);
    } else {
      // Normal case: user is within their plan limit, no extra credits
      const usedPercentage = totalExecutions > 0 ? (used / totalExecutions) * 100 : 0;
      const remainingPercentage =
        totalExecutions > 0 ? ((totalExecutions - used) / totalExecutions) * 100 : 100;

      setUsedBarWidth(usedPercentage);
      setRemainingBarWidth(remainingPercentage);
    }
  }, [used, totalExecutions, extraExecutions, totalAvailableExecutions]);

  useEffect(() => {
    if (showManageSubscriptions) {
      setTimeout(() => {
        const element = document.getElementById("manage-subscriptions");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 3000);
    }
  }, [showManageSubscriptions]);

  if (isLoading) {
    return <ComponentLoader label={"Loading..."} />;
  }

  if (hasError || error) {
    // Check if it's the new API response structure with ok: false
    if (creditUsage?.ok === false) {
      if (creditUsage?.expired === true) {
        return (
          <div>
            <PlanEmptyScreen
              errorType="expired_plan"
              endDate={creditUsage?.plan_end_date}
              onPurchasePlan={() => setShowManageSubscriptions(true)}
            />
            {showManageSubscriptions && (
              <div className="al-w-full">
                <SliderSelect
                  initialPlanName={creditUsage.credit_type}
                  initialTenure={currentTenure}
                  currentPlan={currentPlan}
                  setPlanName={setCurrentPlan}
                  planChangeState={planChangeState}
                  setPlanChangeState={setPlanChangeState}
                  setShowSuccessMessage={setPostTransactionMessage}
                  planToEditionMap={creditUsage.plan_to_edition_map}
                  downgradeInfo={creditUsage.downgraded_info}
                  cancelPlanDowngradeMutation={cancelPlanDowngradeMutation}
                />
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div>
            <PlanEmptyScreen
              errorType="no_plan"
              onPurchasePlan={() => setShowManageSubscriptions(true)}
            />
            {showManageSubscriptions && (
              <div className="al-w-full">
                <SliderSelect
                  initialPlanName={creditUsage.credit_type}
                  initialTenure={currentTenure}
                  currentPlan={currentPlan}
                  setPlanName={setCurrentPlan}
                  planChangeState={planChangeState}
                  setPlanChangeState={setPlanChangeState}
                  setShowSuccessMessage={setPostTransactionMessage}
                  planToEditionMap={creditUsage.plan_to_edition_map}
                  downgradeInfo={creditUsage.downgraded_info}
                  cancelPlanDowngradeMutation={cancelPlanDowngradeMutation}
                />
              </div>
            )}
          </div>
        );
      }
    }

    // Fallback for actual errors (network issues, etc.)
    return (
      <EmptyScreen
        title="Plans Not Available"
        subTitle="Unable to load plans and credit information. Please contact your administrator or try again later."
      />
    );
  }

  // Token usage computed values
  const overageRate =
    tokenUsage?.overage_rate_cents_per_million ?? walletData?.overage_rate_cents_per_million ?? 0;
  const walletBalance = walletData?.balance_cents ?? 0;
  const walletTokenEquivalent = walletData?.balance_tokens ?? 0;

  // Use aggregate totals from backend (grant + monthly allowance + wallet)
  const overageTokens = tokenUsage?.overage_tokens ?? 0;
  const totalTokenPool = (tokenUsage?.total_tokens ?? 0) + walletTokenEquivalent;
  const tokensConsumed = (tokenUsage?.total_consumed ?? 0) + overageTokens;
  const totalTokenCapacity = totalTokenPool;
  const grantTotal = tokenUsage?.grant_tokens_total ?? 0;
  const hasTokenCapacity = totalTokenCapacity > 0 || tokensConsumed > 0;
  const tokenUsagePercent =
    totalTokenCapacity > 0 ? Math.min(100, (tokensConsumed / totalTokenCapacity) * 100) : 0;

  // Plan type detection
  const isCommunity = creditUsage?.credit_type === "free100";
  const isOneTimeGrant = isCommunity && grantTotal > 0;

  return (
    <div className="al-space-y-6">
      {postTransactionMessage && (
        <InfoTooltip
          handleSuccessMessageClose={handleSuccessMessageClose}
          message={postTransactionMessage.message}
          type={postTransactionMessage.type}
          pageMessage={postTransactionMessage.pageMessage}
          showCloseButton={postTransactionMessage.showCloseButton}
        />
      )}

      {/* Credits Summary Section */}
      <div className="al-bg-white al-rounded-lg al-border al-p-6">
        <Stack direction="row" justify="between" align="center" className="al-mb-4">
          <h2 className="al-text-lg al-font-semibold al-text-gray-900">Credits Summary</h2>
          {!isLoading && (
            <div className="al-text-sm al-text-gray-600">
              Current Plan: <span className="al-font-medium">{creditUsage?.current_edition}</span>
            </div>
          )}
        </Stack>

        <Stack direction="row" gap="lg">
          {/* Pie Chart - 1/3 width */}
          <Stack className="al-w-1/3" justify="center" align="center">
            <CustomPieChart
              data={data}
              infoText={extraExecutions ? "Extra" : "Remaining"}
              infoData={extraExecutions || totalAvailableExecutions}
              customStyle={extraExecutions ? "extra" : ""}
            />
          </Stack>

          {/* Credits Section - 2/3 width in Card */}
          <div className="al-w-2/3">
            <Card className="al-m-4">
              <CardContent className="!al-p-4">
                <div className="al-space-y-4">
                  <div>
                    <Stack direction="row" justify="between" align="center" className="al-mb-2">
                      <h3 className="al-text-sm al-font-medium al-text-gray-700">Credits</h3>
                      {!isLoading && (
                        <div className="al-text-xs al-text-gray-500">
                          {creditUsage?.current_start_date} - Today
                        </div>
                      )}
                    </Stack>

                    <div className="al-relative al-bg-gray-200 al-rounded-full al-h-4 al-overflow-hidden">
                      <Tooltip title={<span>{used} Used Credits</span>}>
                        <Stack gap="none" className="al-h-4">
                          {/* Used Credits (Orange) */}
                          <div
                            className="al-bg-orange-500 al-transition-all"
                            style={{ width: `${usedBarWidth}%` }}
                          />
                          {/* Remaining Credits (Blue) */}
                          <div
                            className={classNames("al-transition-all", {
                              "al-bg-blue-500": !extraExecutions,
                              "al-bg-red-500": extraExecutions,
                            })}
                            style={{ width: `${remainingBarWidth}%` }}
                          />
                        </Stack>
                      </Tooltip>
                    </div>

                    <Stack
                      direction="row"
                      justify="between"
                      className="al-text-xs al-text-gray-500 al-mt-1"
                    >
                      <span>0</span>
                      <span>{extraExecutions > 0 ? used + extraExecutions : totalExecutions}</span>
                    </Stack>
                  </div>

                  {/* Legends */}
                  <Stack direction="row" gap="md" wrap className="al-text-xs">
                    <Stack direction="row" gap="xs" align="center">
                      <div className="al-w-3 al-h-3 al-bg-orange-500 al-rounded"></div>
                      <span>Used Credits</span>
                    </Stack>
                    <Stack direction="row" gap="xs" align="center">
                      <div className="al-w-3 al-h-3 al-bg-blue-500 al-rounded"></div>
                      <span>Remaining Credits</span>
                    </Stack>
                    <Stack direction="row" gap="xs" align="center">
                      <div className="al-w-3 al-h-3 al-bg-red-500 al-rounded"></div>
                      <span>Extra Credits</span>
                    </Stack>
                  </Stack>

                  {!isLoading && !isEnterprise && (
                    <div className="al-text-sm al-text-gray-600">
                      Grant Remaining: {availableGrantExecutions} credits
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </Stack>
      </div>
      {/* Subscription Notes */}
      {!isLoading && (
        <div className="al-bg-white al-rounded-lg al-border al-p-6">
          {isEnterprise ? (
            <div className="al-text-sm al-text-gray-700">
              <span className="al-font-semibold">Note:</span> Your current grant of{" "}
              <span className="al-font-medium al-text-blue-600">{totalPlanExecutions} credits</span>{" "}
              will expire on{" "}
              <span className="al-font-medium al-text-blue-600">{nextRefreshDate}</span>.{" "}
            </div>
          ) : extraExecutions ? (
            <div className="al-bg-yellow-50 al-border al-border-yellow-200 al-rounded-lg al-p-4">
              <div className="al-text-sm al-text-yellow-800">
                <span className="al-font-semibold">Note:</span> Your current subscription limit of{" "}
                {totalPlanExecutions} is reached.{" "}
                {creditUsage?.downgraded_info ? (
                  <span>
                    Your Plan will be downgraded to {creditUsage.downgraded_info.plan_display_name}{" "}
                    on {creditUsage.downgraded_info.current_subscription_end_date}.
                  </span>
                ) : (
                  <span>
                    Your subscription of {totalPlanExecutions} will renew automatically on{" "}
                    <span className="al-font-medium al-text-blue-600">{nextRefreshDate}</span>. If
                    needed, please upgrade your subscription
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="al-text-sm al-text-gray-700">
              <span className="al-font-semibold">Note:</span> Your subscription of{" "}
              {totalPlanExecutions} monthly credits will renew on{" "}
              <span className="al-font-medium al-text-blue-600">{nextRefreshDate}</span>.{" "}
              {creditUsage?.downgraded_info && (
                <span className="al-text-yellow-600">
                  A downgrade is Scheduled at{" "}
                  {creditUsage.downgraded_info.current_subscription_end_date} and the current plan
                  will be downgraded to {creditUsage.downgraded_info.plan_display_name}.
                </span>
              )}
            </div>
          )}

          {creditUsage?.downgraded_info && (
            <Button
              variant="outline"
              className="al-mt-3"
              onClick={() => {
                try {
                  cancelPlanDowngradeMutation.mutate();
                } catch (error) {
                  console.error("Error cancelling plan downgrade:", error);
                  setPostTransactionMessage({
                    message:
                      "Failed to cancel plan downgrade. Please try again or contact support.",
                    type: "error",
                    pageMessage: true,
                    showCloseButton: true,
                  });
                }
              }}
            >
              Cancel Downgrade
            </Button>
          )}
        </div>
      )}

      {/* Token Usage */}
      {!isLoading && !isEnterprise && (
        <div className="al-bg-white al-rounded-lg al-border al-p-5">
          <Stack direction="row" justify="between" align="center" className="al-mb-3">
            <Stack direction="row" gap="xs" align="center">
              <span className="al-text-sm al-font-medium al-text-gray-900">Token Usage</span>
              {isOneTimeGrant && (
                <span className="al-text-xs al-text-gray-400 al-italic">(one-time grant)</span>
              )}
            </Stack>
            {tokenUsage?.period_start && tokenUsage?.period_end && (
              <span className="al-text-xs al-text-gray-400">
                {new Date(tokenUsage.period_start).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}{" "}
                —{" "}
                {new Date(tokenUsage.period_end).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            )}
          </Stack>

          {hasTokenCapacity ? (
            <>
              <div className="al-bg-gray-100 al-rounded-full al-h-2 al-overflow-hidden al-mb-2">
                <div
                  className={classNames("al-h-2 al-rounded-full al-transition-all", {
                    "al-bg-blue-500": tokenUsagePercent < 90,
                    "al-bg-orange-500": tokenUsagePercent >= 90 && tokenUsagePercent < 100,
                    "al-bg-red-500": tokenUsagePercent >= 100,
                  })}
                  style={{ width: `${tokenUsagePercent}%` }}
                />
              </div>

              <Stack
                direction="row"
                justify="between"
                className="al-text-xs al-text-gray-500 al-mb-3"
              >
                <span>
                  {formatTokens(tokensConsumed)} / {formatTokens(totalTokenCapacity)} tokens
                </span>
                <span>
                  {formatTokens(Math.max(0, totalTokenCapacity - tokensConsumed))} remaining
                </span>
              </Stack>

              {walletBalance > 0 && walletTokenEquivalent > 0 && (
                <div className="al-text-xs al-text-gray-500">
                  Wallet: {formatTokens(walletTokenEquivalent)} tokens ($
                  {(walletBalance / 100).toFixed(2)})
                </div>
              )}
            </>
          ) : (
            <div className="al-text-sm al-text-gray-500">
              {isCommunity
                ? "Your one-time token grant is fully used. Connect your own API key (BYOK) to continue using AI features."
                : "No tokens available. Deposit funds for overage tokens or upgrade your plan."}
            </div>
          )}
        </div>
      )}

      {/* Overage Status Warning */}
      {!isLoading && !isEnterprise && tokenUsage && (
        <OverageStatusCard
          tokenUsage={tokenUsage}
          walletData={walletData}
          isCommunity={!!isCommunity}
        />
      )}

      {/* Manage Subscriptions */}
      {!isLoading && (
        <div id="manage-subscriptions" className="al-bg-white al-rounded-lg al-border al-p-6">
          <Stack direction="row" justify="between" align="center">
            <h2 className="al-text-lg al-font-semibold al-text-gray-900">Need more credits?</h2>
            {isEnterprise ? (
              <Button onClick={show}>Contact Us</Button>
            ) : (
              <Button onClick={() => setShowManageSubscriptions(true)}>Manage Subscription</Button>
            )}
          </Stack>
        </div>
      )}

      {cancelPlanDowngradeMutation.isPending && <ComponentLoader label={"Downgrading..."} />}

      {!isLoading && showManageSubscriptions && (
        <div className="al-w-full">
          <SliderSelect
            initialPlanName={creditUsage?.credit_type}
            initialTenure={currentTenure}
            currentPlan={currentPlan}
            setPlanName={setCurrentPlan}
            planChangeState={planChangeState}
            setPlanChangeState={setPlanChangeState}
            setShowSuccessMessage={setPostTransactionMessage}
            planToEditionMap={creditUsage?.plan_to_edition_map}
            downgradeInfo={creditUsage?.downgraded_info}
            cancelPlanDowngradeMutation={cancelPlanDowngradeMutation}
          />
        </div>
      )}

      {/* Overage — Deposit Funds */}
      {!isLoading && !isEnterprise && (
        <div id="add-tokens" className="al-bg-white al-rounded-lg al-border al-p-5">
          <Stack direction="row" justify="between" align="center">
            <div>
              <span className="al-text-sm al-font-medium al-text-gray-900">Overage</span>
              {overageRate > 0 && !isCommunity && (
                <span className="al-text-xs al-text-gray-400 al-ml-2">
                  {formatTokens(Math.floor((depositAmount * 100 * 1_000_000) / overageRate))} tokens
                  per ${depositAmount} at ${(overageRate / 100).toFixed(2)}/M
                </span>
              )}
              {isCommunity && (
                <span className="al-text-xs al-text-gray-400 al-ml-2">
                  Overage is not available on the Community plan
                </span>
              )}
            </div>
            <Stack direction="row" gap="sm" align="center">
              <Stack direction="row" gap="xs" align="center">
                <span className="al-text-sm al-text-gray-500">$</span>
                <input
                  type="number"
                  min={5}
                  max={10000}
                  value={depositAmount}
                  onChange={e =>
                    setDepositAmount(Math.max(5, Math.min(10000, Number(e.target.value))))
                  }
                  className="al-w-20 al-border al-rounded al-px-2 al-py-1 al-text-sm"
                  disabled={!!isCommunity}
                />
              </Stack>
              <Stack direction="row" align="center" gap="sm">
                <Tooltip
                  title={
                    isCommunity
                      ? "You're on the Community plan. Upgrade your plan to add overage funds."
                      : ""
                  }
                >
                  <span>
                    <Button
                      size="sm"
                      onClick={() =>
                        walletDepositMutation.mutate({ amountCents: depositAmount * 100 })
                      }
                      disabled={!!isCommunity || walletDepositMutation.isPending}
                    >
                      {walletDepositMutation.isPending ? "Redirecting..." : "Deposit"}
                    </Button>
                  </span>
                </Tooltip>
                {walletDepositMutation.isError && (
                  <span className="al-text-xs al-text-red-600">
                    Failed to start deposit. Please try again.
                  </span>
                )}
              </Stack>
            </Stack>
          </Stack>
        </div>
      )}

      {/* Transactions */}
      {walletData?.transactions && walletData.transactions.length > 0 && (
        <div className="al-bg-white al-rounded-lg al-border al-p-5">
          <span className="al-text-sm al-font-medium al-text-gray-900">Transactions</span>
          <div className="al-mt-3">
            {walletData.transactions
              .filter((txn: WalletTransaction) => txn.transaction_type !== "deduction")
              .slice(0, 5)
              .map((txn: WalletTransaction) => (
                <Stack
                  key={txn.id}
                  direction="row"
                  justify="between"
                  align="center"
                  className="al-py-2 al-border-b al-border-gray-50 last:al-border-0"
                >
                  <span className="al-text-xs al-text-gray-600">
                    {txn.description ?? txn.transaction_type}
                    <span className="al-text-gray-400 al-ml-2">
                      {new Date(txn.created_on).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </span>
                  <span
                    className={classNames("al-text-xs al-font-medium", {
                      "al-text-green-600": txn.amount_cents > 0,
                      "al-text-red-600": txn.amount_cents < 0,
                    })}
                  >
                    {txn.tokens != null && txn.tokens !== 0
                      ? `${txn.tokens > 0 ? "+" : ""}${formatTokens(Math.abs(txn.tokens))} tokens`
                      : `${txn.amount_cents > 0 ? "+" : ""}$${(Math.abs(txn.amount_cents) / 100).toFixed(2)}`}
                  </span>
                </Stack>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
